import type React from 'react';
import type { HTMLAttributes, ReactNode, SyntheticEvent } from 'react';
import {
  Autocomplete,
  type AutocompleteChangeDetails,
  type AutocompleteChangeReason,
  type AutocompleteRenderInputParams,
  type AutocompleteRenderOptionState,
  type AutocompleteValue,
  CircularProgress,
  InputAdornment,
  Popper,
} from '@mui/material';
import { Search } from '@mui/icons-material';

import dropdownIcon from '../../../../images/dropdown.svg';
import { toArray } from '../../../../tools/utilities';
import BasicTextField from '../BasicTextField/BasicTextField';
import FslTextField from '../FslTextField/FslTextField';

// You can keep your default equality checker or update it as needed.
function defaultGetOptionSelected(option: any, value: any) {
  if (typeof option !== typeof value) return false;
  if (option === value) return true;

  if (typeof option !== 'object') return false;

  return Object.keys(option)
    .concat(Object.keys(value))
    .map((k) => option[k] === value[k])
    .every((v) => v);
}

export interface ISelectWithSearchProps<T, Multiple extends boolean = false> {
  title?: string;
  placeholder?: string;
  className?: string;
  iconPosition?: 'start' | 'end';
  value?: T | T[]; // simplified alias for AutocompleteValue<T, Multiple, false, false>
  inputValue?: string;
  options?: T[];
  filterOptions?: (options: T[], state: { inputValue: string }) => T[];
  onChange?: (
    event: SyntheticEvent<Element, Event>,
    value: AutocompleteValue<T, Multiple, boolean, false>,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<T> | undefined
  ) => void;
  onInputChange?: (event: SyntheticEvent, value: string, reason: string) => void;
  getOptionLabel?: (option: T) => string;
  renderOption?: (props: HTMLAttributes<HTMLLIElement>, option: T, state: AutocompleteRenderOptionState) => ReactNode;
  inputFieldId?: string;
  isLoading?: boolean;
  noOptionsText?: string;
  isClearable?: boolean;
  clearOnBlur?: boolean;
  getOptionSelected?: (option: T, value: T) => boolean;
  popupIcon?: ReactNode;
  hideIcon?: boolean;
  hidePopupIcon?: boolean;
  inputAdornmentIcon?: ReactNode;
  autoFocus?: boolean;
  isDisabled?: boolean;
  helperText?: string;
  error?: boolean;
  isReadOnly?: boolean;
  required?: boolean;
  multiple?: Multiple;
  blurOnSelect?: boolean;
  open?: boolean;
  onOpen?: (event: SyntheticEvent) => void;
  onClose?: (event: SyntheticEvent, reason: string) => void;
  updateDelay?: number;
}

export default function SelectWithSearch<T, Multiple extends boolean = false>({
  iconPosition,
  inputFieldId,
  title = '',
  placeholder,
  className,
  value,
  options,
  onChange,
  renderOption,
  getOptionLabel,
  onInputChange,
  inputValue,
  filterOptions,
  isLoading,
  noOptionsText,
  isClearable,
  clearOnBlur,
  getOptionSelected,
  popupIcon,
  hideIcon,
  inputAdornmentIcon,
  hidePopupIcon,
  autoFocus,
  isDisabled,
  helperText,
  error,
  isReadOnly,
  required,
  multiple,
  blurOnSelect,
  open,
  onClose,
  onOpen,
  updateDelay,
}: ISelectWithSearchProps<T, Multiple>) {
  if (isReadOnly && !multiple) {
    return (
      <BasicTextField
        id={inputFieldId}
        label={title}
        className={className}
        autoFocus={autoFocus}
        disabled={isDisabled}
        error={error}
        helperText={helperText}
        isReadOnly={isReadOnly}
        value={(value ? getOptionLabel?.(value as T) : value) ?? null}
        required={required}
      />
    );
  }

  return (
    <Autocomplete<T, Multiple, boolean, false>
      multiple={multiple}
      style={{ width: '100%' }}
      value={value as AutocompleteValue<T, Multiple, boolean, false> | undefined}
      disabled={isDisabled}
      options={options ?? (toArray(value) as T[])}
      filterOptions={filterOptions}
      onChange={onChange}
      inputValue={inputValue}
      onInputChange={onInputChange}
      clearOnBlur={clearOnBlur}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      loading={isLoading}
      loadingText="Henter..."
      disableClearable={!isClearable}
      noOptionsText={noOptionsText ?? (!inputValue ? 'Foretag søgning' : 'Ingen resultater')}
      // Note: In MUI v5, use `isOptionEqualToValue` instead of `getOptionSelected`
      isOptionEqualToValue={getOptionSelected ?? defaultGetOptionSelected}
      popupIcon={!hidePopupIcon ? popupIcon ?? <img src={dropdownIcon} alt="dropdownButton" style={{ width: '15px', height: '15px' }} /> : null}
      blurOnSelect={blurOnSelect}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <FslTextField
          {...params}
          placeholder={placeholder}
          id={inputFieldId}
          label={title}
          className={className}
          style={{ minHeight: '32px' }}
          autoFocus={autoFocus}
          disabled={isDisabled}
          error={error}
          helperText={helperText}
          updateDelay={updateDelay ?? 0}
          InputProps={{
            ...params.InputProps,
            startAdornment: !hideIcon ? (
              <>
                {params.InputProps.startAdornment}
                <InputAdornment position={iconPosition ?? 'start'}>{popupIcon ? inputAdornmentIcon : <Search />}</InputAdornment>
              </>
            ) : (
              params.InputProps.startAdornment
            ),
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      PopperComponent={(props) => {
        return (
          <Popper
            {...props}
            style={{
              minWidth: props.style?.width,
              ...(props.style ?? {}),
              width: 'auto',
            }}
          />
        );
      }}
    />
  );
}
